"use client";

import React from "react";
import NextLink from "next/link";
import MuiLink, { type LinkProps as MuiLinkProps } from "@mui/material/Link";
import { useParams, usePathname } from "next/navigation";
import { localizePath, resolveCurrentLocale } from "@/utils/localize-path";
import type { LocaleCode } from "@/utils/localize-path";
import type { UrlObject } from "url";

type LocalizedLinkProps = Omit<MuiLinkProps, "href"> & {
  href?: string | UrlObject;
};

type LinkLikeHref = string | UrlObject | undefined;

const isUrlObject = (value: unknown): value is UrlObject =>
  Boolean(value && typeof value === "object");

const hasStringPathname = (value: unknown): value is UrlObject & { pathname: string } =>
  isUrlObject(value) && typeof value.pathname === "string";

const isInternalHref = (value: LinkLikeHref) =>
  (typeof value === "string" && value.startsWith("/")) ||
  (hasStringPathname(value) && value.pathname.startsWith("/"));

const normalizeHash = (value?: string | null) => {
  // Ensure hash fragments are emitted with a leading #.
  if (!value) return "";
  return value.startsWith("#") ? value : `#${value}`;
};

const normalizeSearch = (search?: string | null, query?: UrlObject["query"]) => {
  // Preserve explicit search first, otherwise rebuild it from query entries.
  if (search) {
    return search.startsWith("?") ? search : `?${search}`;
  }

  if (!query || typeof query !== "object") {
    return "";
  }

  const params = new URLSearchParams();
  Object.entries(query as Record<string, unknown>).forEach(([key, value]) => {
    if (value == null) return;

    if (Array.isArray(value)) {
      // Repeat query keys for array entries to match URLSearchParams semantics.
      value.forEach((entry) => {
        if (entry != null) {
          params.append(key, String(entry));
        }
      });
      return;
    }

    params.append(key, String(value));
  });

  const queryString = params.toString();
  return queryString ? `?${queryString}` : "";
};

// Convert non-internal UrlObject values to a stable string for native anchor fallback.
const stringifyUrlObject = (href: UrlObject): string | undefined => {
  if (typeof href.href === "string" && href.href.length > 0) {
    return href.href;
  }

  const protocol = typeof href.protocol === "string" ? href.protocol : "";
  const auth = typeof href.auth === "string" && href.auth ? `${href.auth}@` : "";
  const host =
    typeof href.host === "string" && href.host
      ? href.host
      : typeof href.hostname === "string" && href.hostname
        ? `${href.hostname}${typeof href.port === "string" && href.port ? `:${href.port}` : ""}`
        : "";
  const pathname = typeof href.pathname === "string" ? href.pathname : "";
  const search = normalizeSearch(href.search, href.query);
  const hash = normalizeHash(href.hash);

  if (host) {
    const normalizedProtocol = protocol ? (protocol.endsWith(":") ? protocol : `${protocol}:`) : "";
    return `${normalizedProtocol}//${auth}${host}${pathname}${search}${hash}`;
  }

  if (!pathname && !search && !hash) {
    return undefined;
  }

  return `${pathname}${search}${hash}`;
};

const localizeHref = (href: LinkLikeHref, locale: LocaleCode): LinkLikeHref => {
  // Localize string routes and UrlObject pathname while leaving external URLs untouched.
  if (typeof href === "string") {
    return localizePath(href, locale);
  }

  if (hasStringPathname(href)) {
    return {
      ...href,
      pathname: localizePath(href.pathname, locale),
    };
  }

  return href;
};

const resolveFallbackHref = (href: LinkLikeHref): string | undefined => {
  if (typeof href === "string") {
    return href;
  }

  if (isUrlObject(href)) {
    return stringifyUrlObject(href);
  }

  return undefined;
};

const Link = React.forwardRef<HTMLAnchorElement | HTMLButtonElement, LocalizedLinkProps>(
  function Link({ href, ...props }, ref) {
    const params = useParams();
    const pathname = usePathname();
    // Resolve locale from route context so links remain language-scoped.
    const locale = resolveCurrentLocale(params?.lang, pathname);

    const localizedHref = React.useMemo(() => localizeHref(href, locale), [href, locale]);
    const shouldUseNextLink = React.useMemo(() => isInternalHref(localizedHref), [localizedHref]);

    // Internal paths should use NextLink to preserve client-side routing and prefetch behavior.
    if (shouldUseNextLink) {
      return (
        <MuiLink
          ref={ref as React.Ref<HTMLAnchorElement>}
          component={NextLink}
          href={localizedHref as any}
          {...props}
        />
      );
    }

    const fallbackHref = resolveFallbackHref(localizedHref);

    // Without href, render as a button so keyboard users can still focus/activate the control.
    if (!fallbackHref) {
      return (
        <MuiLink
          ref={ref as React.Ref<HTMLButtonElement>}
          component="button"
          type="button"
          {...(props as MuiLinkProps)}
        />
      );
    }

    return (
      <MuiLink
        ref={ref as React.Ref<HTMLAnchorElement>}
        href={fallbackHref}
        {...(props as MuiLinkProps)}
      />
    );
  },
);

export default Link;
